import { useQuery } from '@apollo/client';
import classNames from 'classnames';
import inflection from 'inflection';
import _ from 'lodash';
import React, { useState } from 'react';
import type { GroupBase, SingleValueProps } from 'react-select';
import { components } from 'react-select';
import {
  ProvisionSelectOption,
  ProvisionSelectSingleValue,
  provisionsToOptions,
} from '../admin/provisions/ProvisionSelectOption';
import {
  ProvisionTypeSelectOption,
  ProvisionTypeSelectSingleValue,
} from '../admin/provisions/ProvisionTypeSelectOption';
import useProvisionEntities from '../admin/provisions/useProvisionEntities';
import useProvisionEntitySearchConfig from '../admin/provisions/useProvisionEntitySearchConfig';
import useAppSelector, { useAppDispatch } from '../app/redux/useAppSelector';
import Button from '../components/Button';
import Caret from '../components/Caret';
import Form, { Field, FieldSet, Submit } from '../components/Form';
import LoadingIndicator from '../components/LoadingIndicator';
import Modal, { ModalActionFooter, ModalTitle } from '../components/Modal';
import SVGIcon from '../components/SVGIcon';
import type { OptionTypeBase } from '../components/Select';
import Select from '../components/Select';
import PostMention from '../editor/mentions/PostMention';
import type { ProvisionChange } from '../graphql/generated/graphql';
import {
  InvitationSource,
  PlanTier,
  ProvisionChangeAction,
  ProvisionChangeRelation,
  ProvisionType,
  UserType,
} from '../graphql/generated/graphql';
import caretIcon from '../icons/non-prefilled/16x16/caret.svg';
import EntitySelect from '../search/entitySearch/EntitySelect';
import type { ToastLevel } from '../ui/uiSlice';
import { UIActions } from '../ui/uiSlice';
import handleApolloError from '../utils/error/handleApolloError';
import { nonEmptyId } from '../utils/nonEmpty';
import InvitationGoogleConnect from './InvitationGoogleConnect';
import styles from './InvitationModal.module.scss';
import InvitationSeatCallout from './InvitationSeatCallout';
import type { InvitationUserSelectOptionType } from './InvitationUserSelect';
import InvitationUserSelectField from './InvitationUserSelectField';
import SSOBanner from './SSOBanner';
import SignUpLinkTip from './SignUpLinkTip';
import useInviteMutation from './useInviteMutation';
import useSeatLimits from './useSeatLimits';
import type { DocumentType } from '../graphql/generated/gql';
import { gql } from '../graphql/generated/gql';

const MEMBERSHIP_CONFIG = { group: {}, topic: {}, post: {} };

type InvitationProvisionFormValues = {
  favoritePostIds: string[];
  favoriteTopicIds: string[];
  groupIds: string[];
  name: string;
  postIds: string[];
  topicIds: string[];
  type: ProvisionType;
  urlEnabled: boolean;
};

interface InvitationFormValues {
  invites: InvitationUserSelectOptionType[];
  message: string;
  provisionId: string | null;
  updatedProvision: InvitationProvisionFormValues;
}

const INITIAL_POST_FRAGMENT = gql(`
  fragment InvitationModal_initialPost on Post {
    id
    title
  }
`);

const INVITATION_MODAL_PROVISION_FRAGMENT = gql(`
  fragment InvitationModal_provision on Provision {
    id
    name
    type
    urlEnabled
    isSsoDefault
    favoritePosts {
      id
    }
    favoriteTopics {
      id
    }
    topics {
      id
    }
    groups {
      id
    }
    posts {
      id
    }
    ...ProvisionSelectOption_provision
    ...ProvisionSelectSingleValue_provision
    ...SignUpLinkTip_provision
  }
`);

type ProvisionFragment = DocumentType<
  typeof INVITATION_MODAL_PROVISION_FRAGMENT
>;

const INVITATION_MODAL_QUERY = gql(`
  query InvitationModal {
    session: currentSession {
      organization {
        id
        planTier
        provisions {
          ...InvitationModal_provision
        }
        settings {
          authentication
          requireInviteApproval
        }
      }
    }
    # TODO: Fix this the next time the file is edited.
    # eslint-disable-next-line @graphql-eslint/require-id-when-available
    google: integration(platform: "google") {
      features {
        externalUsers
      }
    }
  }
`);

const DropdownIndicator = () => (
  <div className={styles.dropdownIndicator}>
    <Caret direction="down" />
  </div>
);

const SingleValue = <
  OptionType extends OptionTypeBase = { label: string; value: string },
  IsMulti extends boolean = false,
  GroupType extends GroupBase<OptionType> = GroupBase<OptionType>,
>({
  children,
  ...props
}: SingleValueProps<OptionType, IsMulti, GroupType>) => (
  <components.SingleValue {...props}>
    <div className={styles.selectValue}>{children}</div>
  </components.SingleValue>
);

const constructProvisionChangesForRelation = (
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/default-param-last
  newIds: string[] = [],
  // TODO: Fix this the next time the file is edited.
  // eslint-disable-next-line @typescript-eslint/default-param-last
  oldEntities: { id: string }[] = [],
  relation: ProvisionChangeRelation,
): ProvisionChange[] => {
  const oldIds = oldEntities.map((e) => e.id);
  const addedIds = _.difference(newIds, oldIds);
  const removedIds = _.difference(oldIds, newIds);

  return [
    ...addedIds.map<ProvisionChange>((targetId) => ({
      action: ProvisionChangeAction.Add,
      relation,
      targetId,
    })),
    ...removedIds.map<ProvisionChange>((targetId) => ({
      action: ProvisionChangeAction.Remove,
      relation,
      targetId,
    })),
  ];
};

const getProvisions = (
  provisions: ProvisionFragment[] | null,
  isSeatLimitMaxed: boolean,
  isGuestLimitMaxed: boolean,
  isAdmin: boolean,
) => {
  const filteredProvisions =
    provisions?.filter(nonEmptyId).filter((provision) => {
      const type = provision?.type;
      if (!type) return false;
      if (type === ProvisionType.Guest) return !isGuestLimitMaxed;
      if (type === ProvisionType.Admin) return !isSeatLimitMaxed && isAdmin;
      return !isSeatLimitMaxed;
    }) ?? [];

  return _.sortBy(filteredProvisions, (provision) => [
    [true, false].indexOf(provision.isSsoDefault === true),
    [ProvisionType.User, ProvisionType.Admin, ProvisionType.Guest].indexOf(
      provision.type,
    ),
    provision.name,
  ]);
};

const getCurrentProvisionFormValues = (
  provisionId: string | null,
  provisions: ProvisionFragment[],
  isSeatLimitMaxed: boolean,
  previousValues?: InvitationProvisionFormValues,
): InvitationProvisionFormValues => {
  const provision = provisions?.find((p) => p.id === provisionId) ?? null;

  const defaultProvisionType = isSeatLimitMaxed
    ? ProvisionType.Guest
    : ProvisionType.User;

  return {
    favoritePostIds: provision?.favoritePosts?.map((p) => p.id) ?? [],
    favoriteTopicIds: provision?.favoriteTopics?.map((p) => p.id) ?? [],
    groupIds: provision?.groups?.map((p) => p.id) ?? [],
    name: provision?.name || '',
    postIds: provision?.posts?.map((p) => p.id) ?? [],
    topicIds: provision?.topics?.map((p) => p.id) ?? [],
    type: provision?.type ?? previousValues?.type ?? defaultProvisionType,
    urlEnabled: provision?.urlEnabled ?? false,
  };
};

const InvitationModalProvisionFormFields = ({
  setFieldValue,
  values,
  needApproval,
  originalProvision,
  initialPostId,
  seatLimitMaxed,
}: {
  setFieldValue: (
    field: string,
    value: InvitationFormValues['updatedProvision'][keyof InvitationFormValues['updatedProvision']],
  ) => void;
  values: InvitationFormValues;
  needApproval?: boolean | null;
  originalProvision: ProvisionFragment | null;
  initialPostId?: string;
  seatLimitMaxed: boolean;
}) => {
  const favoriteSearchConfig = useProvisionEntitySearchConfig(
    values.updatedProvision,
  );

  const { favorites, memberships } = useProvisionEntities(
    values.updatedProvision,
  );

  if (!favoriteSearchConfig) {
    return null;
  }

  return (
    <>
      {values.updatedProvision.urlEnabled &&
        !needApproval &&
        originalProvision && (
          <div className={styles.signUpLinkTipContainer}>
            <SignUpLinkTip provision={originalProvision} />
          </div>
        )}
      <Field
        label="Role"
        type="select"
        name="updatedProvision.type"
        isClearable={false}
        options={_.compact([
          !seatLimitMaxed && {
            description: 'Standard team seat',
            value: ProvisionType.User,
          },
          !seatLimitMaxed && {
            description: 'Unlocks all privileges',
            value: ProvisionType.Admin,
          },
          {
            description: 'Limited access to topics',
            value: ProvisionType.Guest,
          },
        ])}
        components={{
          Option: ProvisionTypeSelectOption,
          SingleValue: ProvisionTypeSelectSingleValue,
        }}
      />
      <FieldSet legend="Membership">
        <EntitySelect
          menuPlacement="top"
          isClearable={false}
          onChange={(entities) => {
            const {
              group = [],
              post = [],
              topic = [],
            } = _.groupBy(entities, 'type');

            setFieldValue(
              'updatedProvision.groupIds',
              group.map((e) => e.id),
            );
            setFieldValue(
              'updatedProvision.postIds',
              post.map((e) => e.id),
            );
            setFieldValue(
              'updatedProvision.topicIds',
              topic.map((e) => e.id),
            );
          }}
          config={
            initialPostId
              ? {
                  ...MEMBERSHIP_CONFIG,
                  post: { filter: ({ id }) => id !== initialPostId },
                }
              : MEMBERSHIP_CONFIG
          }
          value={memberships}
          isMulti
        />
      </FieldSet>
      <div className={styles.section}>
        <FieldSet legend="Favorites">
          <EntitySelect
            isClearable={false}
            onChange={(entities) => {
              const { post = [], topic = [] } = _.groupBy(entities, 'type');

              setFieldValue(
                'updatedProvision.favoritePostIds',
                post.map((e) => e.id),
              );
              setFieldValue(
                'updatedProvision.favoriteTopicIds',
                topic.map((e) => e.id),
              );
            }}
            value={favorites}
            isMulti
            {...favoriteSearchConfig}
          />
        </FieldSet>
      </div>
    </>
  );
};

interface InvitationModalProps {
  close: () => void;
  expanded?: boolean;
  initialOptions?: InvitationUserSelectOptionType[];
  initialPost?: DocumentType<typeof INITIAL_POST_FRAGMENT>;
}

const InvitationModal = ({
  close,
  expanded = false,
  initialOptions = [],
  initialPost,
}: InvitationModalProps) => {
  const dispatch = useAppDispatch();
  const [isExpanded, setIsExpanded] = useState(expanded);
  const [initialPostRelation, setInitialPostRelation] = useState<
    ProvisionChangeRelation.PostEditor | ProvisionChangeRelation.PostViewer
  >(ProvisionChangeRelation.PostEditor);
  const invite = useInviteMutation();

  const currentUser = useAppSelector((state) => state.session.user);

  const isAdmin = currentUser.type === UserType.Admin;

  const { data, loading } = useQuery(INVITATION_MODAL_QUERY, {
    fetchPolicy: 'network-only',
    errorPolicy: 'all',
  });

  const { session, google } = data ?? {};
  const {
    isSeatLimitMaxed,
    isGuestLimitMaxed,
    isLoading: isSeatLimitsLoading,
  } = useSeatLimits();

  const isLoading = loading || isSeatLimitsLoading;

  const isGoogleConnectAllowed =
    !isLoading &&
    isAdmin &&
    (!google ||
      (google.features && google.features.externalUsers === 'DISABLED'));

  const provisions = getProvisions(
    session?.organization?.provisions ?? [],
    isSeatLimitMaxed,
    isGuestLimitMaxed,
    isAdmin,
  );

  // eslint-disable-next-line @typescript-eslint/naming-convention -- Add boolean prefix next time file is edited
  const requireInviteApprovalSetting =
    session?.organization?.settings?.requireInviteApproval;
  const planTier = session?.organization?.planTier;

  const sendInvite = async (
    email: string,
    name: string | null,
    provisionId: string | null,
    message: string,
    updatedProvision: InvitationProvisionFormValues | null,
  ) => {
    try {
      const originalProvision =
        provisions?.find((p) => p.id === provisionId) ?? null;

      const changes = [
        ...constructProvisionChangesForRelation(
          updatedProvision?.favoritePostIds,
          originalProvision?.favoritePosts ?? [],
          ProvisionChangeRelation.FavoritePost,
        ),
        ...constructProvisionChangesForRelation(
          updatedProvision?.favoriteTopicIds,
          originalProvision?.favoriteTopics,
          ProvisionChangeRelation.FavoriteTopic,
        ),
        ...constructProvisionChangesForRelation(
          updatedProvision?.topicIds,
          originalProvision?.topics,
          ProvisionChangeRelation.Topic,
        ),
        ...constructProvisionChangesForRelation(
          updatedProvision?.groupIds,
          originalProvision?.groups,
          ProvisionChangeRelation.Group,
        ),
        ...constructProvisionChangesForRelation(
          updatedProvision?.postIds,
          originalProvision?.posts,
          ProvisionChangeRelation.PostEditor,
        ),
        ...constructProvisionChangesForRelation(
          initialPost ? [initialPost.id] : [],
          originalProvision?.posts,
          initialPostRelation,
        ),
      ];

      return await invite({
        variables: {
          email,
          name,
          provisionId,
          provisionChanges:
            changes.length > 0 ||
            updatedProvision?.type !== originalProvision?.type
              ? {
                  changes,
                  type: updatedProvision?.type,
                }
              : undefined,
          message,
          source: initialPost ? InvitationSource.Post : undefined,
        },
      });
    } catch (error) {
      const label = name ?? email;
      const getHandler =
        (toastMessage: string, level: ToastLevel = 'warning') =>
        () =>
          dispatch(UIActions.pushToast({ level, message: toastMessage }));

      handleApolloError(error, {
        USER_EXISTS: getHandler(`${label} is already on your team.`),
        USER_INVITED: getHandler(`${label} is already invited.`),
        RATE_LIMIT: getHandler(
          `You sent too many invites. ${label} has not been invited!`,
          'error',
        ),
      });
      return false;
    }
  };

  const isApprovalNeeded = !isAdmin && requireInviteApprovalSetting;

  const onSubmit = async ({
    provisionId,
    invites,
    message,
    updatedProvision,
  }: InvitationFormValues) => {
    const results = await Promise.all(
      invites.map((item) =>
        sendInvite(
          item.value,
          item.label !== item.value ? item.label : null,
          provisionId,
          message,
          updatedProvision,
        ),
      ),
    );
    const sentInvites = invites.filter((_result, index) => results[index]);
    const totalSent = sentInvites.length;
    if (totalSent > 0) {
      const toastMessage = isApprovalNeeded
        ? `${totalSent} ${inflection.inflect(
            'invitation request',
            totalSent,
          )} sent to your team admins`
        : `Invitations sent to ${totalSent} ${inflection.inflect(
            'teammate',
            totalSent,
          )}.`;

      dispatch(
        UIActions.pushToast({ message: toastMessage, level: 'success' }),
      );
    }
    close();
  };

  const isFormDisabled = isSeatLimitMaxed && planTier === PlanTier.Free;

  if (isLoading) return <LoadingIndicator delay={0} card />;

  return (
    <Modal>
      <ModalTitle close={close}>
        {isApprovalNeeded ? 'Request an invitation' : 'Invite teammates'}
      </ModalTitle>
      <Form<InvitationFormValues>
        initialValues={{
          invites: initialOptions,
          message: '',
          updatedProvision: getCurrentProvisionFormValues(
            null,
            provisions,
            isSeatLimitMaxed,
          ),
          provisionId: null,
        }}
        onSubmit={onSubmit}
      >
        {({ values, setFieldValue, setValues }) => {
          const originalProvision =
            provisions?.find((p) => p.id === values.provisionId) ?? null;

          const inviteCount = values.invites.length;

          const isDisabled = isFormDisabled || inviteCount === 0;

          return (
            <>
              {isSeatLimitMaxed && (
                <div className={styles.bannerContainer}>
                  <InvitationSeatCallout
                    guestLimitReached={isGuestLimitMaxed}
                  />
                </div>
              )}
              <div className={styles.bannerContainer}>
                <SSOBanner
                  provisionType={values.updatedProvision?.type}
                  authenticationSetting={
                    session?.organization?.settings?.authentication
                  }
                />
              </div>
              {isGoogleConnectAllowed && (
                <div className={styles.bannerContainer}>
                  <InvitationGoogleConnect />
                </div>
              )}
              <FieldSet>
                <InvitationUserSelectField
                  value={values.invites}
                  onChange={(v) => setFieldValue('invites', v)}
                  disabled={isSeatLimitMaxed && isGuestLimitMaxed}
                />
              </FieldSet>

              {initialPost && (
                <div className={styles.initialPost}>
                  {inflection.inflect('Invitee', inviteCount)} will be{' '}
                  <div className={styles.selectWrapper}>
                    <Select
                      onChange={(value) => setInitialPostRelation(value)}
                      className={styles.select}
                      isSearchable={false}
                      options={[
                        {
                          value: ProvisionChangeRelation.PostEditor,
                          label: inflection.inflect('Editor', inviteCount),
                        },
                        {
                          value: ProvisionChangeRelation.PostViewer,
                          label: inflection.inflect('Reader', inviteCount),
                        },
                      ]}
                      value={initialPostRelation}
                      components={{ DropdownIndicator, SingleValue }}
                    />
                  </div>
                  for&nbsp;
                  <PostMention
                    link={false}
                    showHoverCard
                    id={initialPost.id}
                    content={initialPost.title}
                    hideIcon
                  />
                </div>
              )}

              {!isExpanded && (
                <>
                  <div className={styles.note}>
                    Teammates will be invited as{' '}
                    <strong>
                      {values.updatedProvision.type === ProvisionType.Guest &&
                        'Guest'}
                      {values.updatedProvision.type === ProvisionType.User &&
                        'Standard User'}
                    </strong>{' '}
                    unless you customize the invitation
                  </div>
                  <Button
                    variant="secondary"
                    block
                    onClick={() => setIsExpanded(true)}
                    disabled={isSeatLimitMaxed && isGuestLimitMaxed}
                  >
                    Show additional settings
                    <SVGIcon className={styles.buttonCaret} size={8}>
                      {caretIcon}
                    </SVGIcon>
                  </Button>
                </>
              )}
              <div
                className={classNames(styles.expandableSection, {
                  [styles.expanded]: isExpanded,
                })}
              >
                {provisions.length > 0 && (
                  <Field
                    name="provisionId"
                    options={provisionsToOptions(provisions)}
                    type="select"
                    label="Provision"
                    disabled={isFormDisabled}
                    isClearable
                    placeholder="No provision assigned"
                    onChange={(newId) =>
                      setValues({
                        ...values,
                        provisionId: newId,
                        updatedProvision: getCurrentProvisionFormValues(
                          newId,
                          provisions,
                          isSeatLimitMaxed,
                          values.updatedProvision,
                        ),
                      })
                    }
                    components={{
                      Option: ProvisionSelectOption,
                      SingleValue: ProvisionSelectSingleValue,
                    }}
                  />
                )}
                <InvitationModalProvisionFormFields
                  setFieldValue={setFieldValue}
                  values={values}
                  needApproval={isApprovalNeeded}
                  originalProvision={originalProvision}
                  initialPostId={initialPost?.id}
                  seatLimitMaxed={isSeatLimitMaxed}
                />
                <Field
                  autoFocus
                  type="textarea"
                  name="message"
                  rows={3}
                  label="Custom Message"
                  placeholder="What should your teammates know about this invite?"
                />
              </div>
              <div className={styles.separator} />
              <ModalActionFooter
                primary={
                  <Submit disabled={isDisabled} size="large">
                    {isApprovalNeeded ? 'Request' : 'Invite'} {inviteCount}{' '}
                    {inflection.inflect('teammate', inviteCount)}
                  </Submit>
                }
              />
            </>
          );
        }}
      </Form>
    </Modal>
  );
};

export default InvitationModal;
